import { useMemo, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { Search, Trash2 } from "lucide-react";
import {
  useDocumentsQuery,
  useLibrarySearchHistoryQuery,
  useLibrarySearchMutation,
  useRemoveDocumentMutation,
} from "@/lib/queries";
import { useSessionQuery } from "@/lib/queries";
import type { LibraryDocument } from "@/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Chip } from "@/components/ui/chip";
import { Checkbox } from "@/components/ui/checkbox";
import { RangeSlider } from "@/components/ui/range-slider";
import { ActiveFilters, FilterBar, FilterPopover, type ActiveFilter } from "@/components/ui/filter-bar";
import {
  ROW_LINK,
  SortableHead,
  Table,
  TableBody,
  TableCell,
  TableEmpty,
  TableHead,
  TableHeader,
  TableRow,
  useSort,
} from "@/components/ui/table";
import { Page, PageFill, PageHeader, Panel, PanelBody, PanelHeader, PanelTitle } from "@/components/layout/Page";
import { datedFilename, downloadCsv } from "@/lib/csv";
import { useMediaQuery, XXL } from "@/lib/use-media-query";

export const Route = createFileRoute("/library/")({
  // ?tags=… so a tag can be linked to rather than only clicked. The tag
  // management page links here to show exactly the documents a tag covers,
  // which is the only way to check what a tag actually means.
  validateSearch: (search: Record<string, unknown>): { tags?: string[] } => {
    const raw = search.tags;
    const tags = Array.isArray(raw)
      ? raw.filter((t): t is string => typeof t === "string")
      : typeof raw === "string"
        ? [raw]
        : [];
    return tags.length > 0 ? { tags } : {};
  },
  component: LibraryIndexPage,
});

type SortKey = "title" | "year" | "pages" | "added";

function LibraryIndexPage() {
  const navigate = useNavigate();
  const { tags: tagsFromUrl } = Route.useSearch();
  const documentsQuery = useDocumentsQuery();
  // Memoised so the facet useMemos below have a stable dependency. Without
  // it the `?? []` produces a fresh array on every render and both recompute
  // every time anything in this component changes.
  const docs = useMemo(
    () => documentsQuery.data?.documents ?? [],
    [documentsQuery.data],
  );
  const totalDocs = documentsQuery.data?.total ?? docs.length;
  const removeDocument = useRemoveDocumentMutation();
  const session = useSessionQuery();
  const canDelete = session.data ? session.data.user.role !== "member" : false;

  const [tableSearch, setTableSearch] = useState("");
  // Dates and page counts read most usefully newest/largest first; a title
  // reads A–Z. The table primitive knows the rule, so every column on every
  // page agrees about it.
  const { sort, dir, headProps } = useSort<SortKey>("title", "asc", {
    year: "desc",
    pages: "desc",
    added: "desc",
  });
  const [audienceFilter, setAudienceFilter] = useState<string[]>([]);
  // Seeded from the URL so a link from the tag page lands with the filter
  // already applied, then owned by the component — clicking a chip afterwards
  // should not need a navigation.
  const [topicFilter, setTopicFilter] = useState<string[]>(tagsFromUrl ?? []);
  // null means "not narrowed". Storing the absence rather than the library's
  // own bounds is what lets the filter tell "everything" apart from "happens
  // to span everything today", which stop being the same answer as soon as a
  // document from a new year is ingested.
  const [yearFilter, setYearFilter] = useState<{ start: number; end: number } | null>(null);
  const [includeUndated, setIncludeUndated] = useState(true);

  const allAudiences = useMemo(() => Array.from(new Set(docs.map((d) => d.audience))).sort(), [docs]);
  const allTopics = useMemo(() => Array.from(new Set(docs.flatMap((d) => d.tags))).sort(), [docs]);
  const yearBounds = useMemo(() => {
    // Year 0 is ERIKA's "no year", kept by the import; as a bound it would
    // start the slider two thousand years before the oldest document.
    const years = docs.map((d) => d.year).filter((y): y is number => typeof y === "number" && y > 0);
    if (years.length === 0) return null;
    return { oldest: Math.min(...years), newest: Math.max(...years) };
  }, [docs]);

  const years = yearFilter ?? (yearBounds ? { start: yearBounds.oldest, end: yearBounds.newest } : null);

  const toggleFilter = (setter: React.Dispatch<React.SetStateAction<string[]>>) => (value: string) => {
    setter((arr) => (arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value]));
  };

  const resetYears = () => {
    setYearFilter(null);
    setIncludeUndated(true);
  };

  const clearAll = () => {
    setAudienceFilter([]);
    setTopicFilter([]);
    resetYears();
    setTableSearch("");
  };

  const visibleDocs: LibraryDocument[] = docs
    .filter((d) => audienceFilter.length === 0 || audienceFilter.includes(d.audience))
    .filter((d) => topicFilter.length === 0 || d.tags.some((t) => topicFilter.includes(t)))
    .filter((d) => {
      // An undated document is an absence, not a year outside the window, so
      // whether it survives is its own decision rather than an accident of
      // comparing null to a number — and it is a decision that stands on its
      // own, without the range having to be narrowed first.
      if (d.year === null || d.year === undefined) return includeUndated;
      if (!yearFilter) return true;
      return d.year >= yearFilter.start && d.year <= yearFilter.end;
    })
    .filter((d) => {
      const q = tableSearch.toLowerCase();
      if (!q) return true;
      // People is a browse dimension in ERIKA — "what do we have that mentions
      // this provost" — so the box searches the extracted entities as well as
      // the title, which is the only way that question can be asked from here.
      const haystack = [
        d.title,
        ...d.tags,
        ...(d.people ?? []),
        ...(d.locations ?? []),
        ...(d.keywords ?? []),
      ];
      return haystack.some((v) => v.toLowerCase().includes(q));
    })
    .sort((a, b) => {
      if (sort.key === "title") return a.title.localeCompare(b.title) * dir;
      if (sort.key === "added") return (Date.parse(a.createdAt) - Date.parse(b.createdAt)) * dir;
      // A document with no year — or no page count — sorts last in either
      // direction rather than being treated as zero, which would put undated
      // documents at the top of an ascending sort.
      const pick = (d: LibraryDocument) => (sort.key === "pages" ? d.pages : d.year);
      const av = pick(a) ?? Number.NEGATIVE_INFINITY;
      const bv = pick(b) ?? Number.NEGATIVE_INFINITY;
      return (av - bv) * dir;
    });

  // Every narrowing in one list, each with the click that undoes exactly it.
  const active: ActiveFilter[] = [
    ...audienceFilter.map((a) => ({
      id: `audience:${a}`,
      label: `Audience: ${a}`,
      onRemove: () => toggleFilter(setAudienceFilter)(a),
    })),
    ...topicFilter.map((t) => ({
      id: `topic:${t}`,
      label: `Topic: ${t}`,
      onRemove: () => toggleFilter(setTopicFilter)(t),
    })),
    ...(yearFilter
      ? [
          {
            id: "years",
            label: `Years: ${yearFilter.start}–${yearFilter.end}${includeUndated ? "" : ", dated only"}`,
            onRemove: resetYears,
          },
        ]
      : !includeUndated
        ? [{ id: "years", label: "Dated documents only", onRemove: resetYears }]
        : []),
    ...(tableSearch
      ? [{ id: "search", label: `“${tableSearch}”`, onRemove: () => setTableSearch("") }]
      : []),
  ];

  return (
    <Page>
      <PageHeader
        title="Library"
        // There is no upload button, and that is not an omission. Documents
        // are produced by the document processor — which extracts page text,
        // rasterises pages and builds vectors — and pushed in over the
        // ingestion API. A browser upload would create a document the
        // processor knows nothing about and no answer could ever cite.
        description="The documents searched when answering questions. They are added by the document processor, so there is nothing to upload here."
      />

      {/*
        Two columns on a wide screen: the semantic search on the left, the
        catalogue on the right. They are two ways of asking the same question —
        "what have we got about X" — and putting them side by side means a
        result can be read against the shelf it came off, instead of the search
        card pushing the table off the bottom of the screen as it used to.

        Not before `2xl`, though. This table declares about 1090px of minimum
        column widths, so anything narrower cannot give a rail 320px without
        putting the table into horizontal scrolling — and a column of entity
        names you have to scroll sideways to read is worse than a search box
        stacked above it. Below that, the search is a card over the table and
        the table still fills the rest of the viewport.
      */}
      <PageFill className="gap-4 2xl:grid 2xl:min-h-0 2xl:grid-cols-[minmax(18rem,20rem)_minmax(0,1fr)] 2xl:items-stretch min-[1800px]:grid-cols-[24rem_minmax(0,1fr)]">
        <NaturalSearch />

        <Panel className="min-h-[24rem] 2xl:min-h-0">
          <PanelHeader>
            <PanelTitle>
              {/* The count says what the filters did. "12 documents" and "12 of
                  240" are different facts and only the second one explains an
                  empty-looking table. */}
              {visibleDocs.length === docs.length
                ? `${docs.length} document${docs.length === 1 ? "" : "s"}`
                : `${visibleDocs.length} of ${docs.length} documents`}
              {/* Only past LIBRARY_LIMIT. The count used to be of what was
                  loaded, and read "100 documents" for a library of thousands. */}
              {totalDocs > docs.length && (
                <span className="ml-2 text-xs font-normal text-amber-text">
                  (only the newest {docs.length.toLocaleString()} of {totalDocs.toLocaleString()} are loaded)
                </span>
              )}
            </PanelTitle>
            <div className="flex items-center gap-2">
              <Input
                value={tableSearch}
                onChange={(e) => setTableSearch(e.target.value)}
                placeholder="Search titles, topics, people…"
                className="h-8 w-56 text-xs lg:w-72"
                aria-label="Search documents"
              />
              {/* The table already refreshes itself every fifty seconds, as
                  ERIKA's did. The button is for the librarian who has just pushed
                  a document in and does not want to wait out the interval. */}
              <Button
                variant="secondary"
                size="sm"
                className="text-xs"
                disabled={documentsQuery.isFetching}
                onClick={() => void documentsQuery.refetch()}
              >
                {documentsQuery.isFetching ? "Refreshing…" : "Refresh"}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                className="text-xs"
                disabled={visibleDocs.length === 0}
                // Exports what is on screen, filters and sort included, rather
                // than the whole library. Someone who has filtered to one topic
                // and clicks Export means that topic.
                onClick={() =>
                  downloadCsv(
                    datedFilename("documents"),
                    [
                      "title",
                      "year",
                      "pages",
                      "audience",
                      "status",
                      "topics",
                      "people",
                      "locations",
                      "keywords",
                      "added",
                    ],
                    visibleDocs.map((d) => [
                      d.title,
                      d.year,
                      d.pages,
                      d.audience,
                      d.status,
                      d.tags.join("; "),
                      (d.people ?? []).join("; "),
                      (d.locations ?? []).join("; "),
                      (d.keywords ?? []).join("; "),
                      d.createdAt.slice(0, 10),
                    ]),
                  )
                }
              >
                Export CSV
              </Button>
            </div>
          </PanelHeader>

          <FilterBar>
            <FilterPopover
              label="Audience"
              count={audienceFilter.length}
              onClear={() => setAudienceFilter([])}
            >
              <div className="flex flex-wrap gap-1.5">
                {allAudiences.length === 0 && (
                  <span className="text-xs text-muted-foreground">Nothing to filter yet.</span>
                )}
                {allAudiences.map((a) => (
                  <Chip
                    key={a}
                    active={audienceFilter.includes(a)}
                    aria-pressed={audienceFilter.includes(a)}
                    onClick={() => toggleFilter(setAudienceFilter)(a)}
                    className="capitalize"
                  >
                    {a}
                  </Chip>
                ))}
              </div>
            </FilterPopover>

            <FilterPopover label="Topics" count={topicFilter.length} onClear={() => setTopicFilter([])}>
              {allTopics.length === 0 ? (
                <span className="text-xs text-muted-foreground">
                  No topics have been assigned yet.
                </span>
              ) : (
                <>
                  <div className="max-h-64 overflow-y-auto">
                    <div className="flex flex-wrap gap-1.5">
                      {allTopics.map((t) => (
                        <Chip
                          key={t}
                          active={topicFilter.includes(t)}
                          aria-pressed={topicFilter.includes(t)}
                          onClick={() => toggleFilter(setTopicFilter)(t)}
                        >
                          {t}
                        </Chip>
                      ))}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="mt-2 text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
                    onClick={() => setTopicFilter(allTopics)}
                  >
                    Select all {allTopics.length}
                  </button>
                </>
              )}
            </FilterPopover>

            {/* The Year column was sortable and not filterable, while the Ask
                page has offered a year range all along. Same control, same
                wording, so the two pages mean the same thing by it. */}
            <FilterPopover
              label="Years"
              summary={
                yearFilter
                  ? `${yearFilter.start}–${yearFilter.end}`
                  : includeUndated
                    ? undefined
                    : "dated only"
              }
              onClear={resetYears}
            >
              {!yearBounds || yearBounds.oldest === yearBounds.newest ? (
                <span className="text-xs text-muted-foreground">
                  {yearBounds
                    ? `Every dated document is from ${yearBounds.oldest}.`
                    : "No document has a year recorded."}
                </span>
              ) : (
                <>
                  <p className="mb-2 font-mono text-xs text-muted-foreground">
                    {years ? `${years.start} – ${years.end}` : "All years"}
                  </p>
                  <RangeSlider
                    label="Document years"
                    min={yearBounds.oldest}
                    max={yearBounds.newest}
                    value={years ?? { start: yearBounds.oldest, end: yearBounds.newest }}
                    onChange={(next) =>
                      // Dragged back out to the full span is not a filter. Keeping
                      // it as one would leave a chip reading "1994–2026" that
                      // removes nothing and starts lying the day a 2027 document
                      // arrives.
                      setYearFilter(
                        next.start === yearBounds.oldest && next.end === yearBounds.newest
                          ? null
                          : next,
                      )
                    }
                  />
                  <label className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
                    <Checkbox
                      checked={includeUndated}
                      onCheckedChange={(c) => setIncludeUndated(c === true)}
                    />
                    Include documents with no year
                  </label>
                </>
              )}
            </FilterPopover>
          </FilterBar>

          <ActiveFilters filters={active} onClearAll={clearAll} />

          {/*
            The page's one scroll region. The table inside it is fluid — no
            fixed widths, no horizontal scrollbar until the columns genuinely
            cannot fit — so the width a wide monitor gives back goes to the
            three entity columns, which are the ones that were unreadably
            narrow before.
          */}
          <PanelBody>
            <Table className="min-w-[68rem]">
              <TableHeader>
                <TableRow>
                  {/* Only the columns whose content has a known size are
                      given one. The five text columns are left to the table's
                      own distribution, so the space a wide window frees up
                      goes to titles and entity lists rather than to padding. */}
                  <SortableHead {...headProps("title", "Title")} className="min-w-[14rem]" />
                  <SortableHead {...headProps("year", "Year")} align="right" className="w-[5.5rem]" />
                  {/* The count that can actually be cited, not the count in
                      the PDF. They differ while a document is mid-ingest. */}
                  <SortableHead {...headProps("pages", "Pages")} align="right" className="w-[5.5rem]" />
                  <TableHead className="w-28">Audience</TableHead>
                  <TableHead className="min-w-[9rem]">Topics</TableHead>
                  <TableHead className="min-w-[8rem]">People</TableHead>
                  <TableHead className="min-w-[8rem]">Locations</TableHead>
                  <TableHead className="min-w-[8rem]">Keywords</TableHead>
                  <SortableHead {...headProps("added", "Added")} className="w-32" />
                  <TableHead className="w-14 text-right">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documentsQuery.isLoading && (
                  <TableEmpty colSpan={10} message="Loading…" />
                )}
                {!documentsQuery.isLoading && visibleDocs.length === 0 && (
                  <TableEmpty
                    colSpan={10}
                    message={
                      docs.length === 0
                        ? "The library is empty. Documents appear here once the document processor has ingested them."
                        : "No document matches these filters."
                    }
                    action={
                      docs.length > 0 && active.length > 0 ? (
                        <Button variant="secondary" size="sm" className="text-xs" onClick={clearAll}>
                          Clear all filters
                        </Button>
                      ) : undefined
                    }
                  />
                )}
                {visibleDocs.map((d) => (
                  <TableRow
                    key={d.id}
                    interactive
                    onClick={() => void navigate({ to: "/library/$documentId", params: { documentId: d.id } })}
                  >
                    <TableCell className="font-medium">
                      {/* The row navigates on click; this is the same
                          destination as something a keyboard can reach and a
                          middle click can open in its own tab. */}
                      <Link
                        to="/library/$documentId"
                        params={{ documentId: d.id }}
                        className={ROW_LINK}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {d.title}
                      </Link>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                      {d.year ?? "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs tabular-nums text-muted-foreground">
                      {d.pages ?? "—"}
                    </TableCell>
                    <TableCell className="capitalize text-muted-foreground">{d.audience}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-1">
                        {/* A document still being indexed is not searchable yet.
                            Saying so is the difference between "missing" and
                            "not ready", which are very different bug reports. */}
                        {d.status !== "ready" && (
                          <Badge tone={d.status === "failed" ? "gray" : "amber"}>
                            {d.status === "failed" ? "Failed" : "Indexing"}
                          </Badge>
                        )}
                        {d.tags.map((t) => (
                          <Badge key={t}>{t}</Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <EntityList items={d.people} />
                    </TableCell>
                    <TableCell>
                      <EntityList items={d.locations} />
                    </TableCell>
                    <TableCell>
                      <EntityList items={d.keywords} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs tabular-nums text-muted-foreground">
                      {new Date(d.createdAt).toLocaleDateString(undefined, { dateStyle: "medium" })}
                    </TableCell>
                    <TableCell className="text-right">
                      {/* Revealed by hovering or focusing the row rather than
                          sitting in a column of its own. A destructive action
                          repeated down every row is a column of invitations to
                          misclick, and it was the first thing in the table —
                          ahead of the title. */}
                      {canDelete && (
                        <button
                          type="button"
                          aria-label={`Remove ${d.title}`}
                          title="Remove this document from the library"
                          className="rounded p-1 text-muted-foreground/0 transition-colors duration-100 ease-snap hover:bg-danger-bg hover:text-danger-text focus-visible:text-danger-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-danger/40 group-hover/row:text-muted-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (
                              window.confirm(
                                `Remove "${d.title}" from the library? Its vectors are deleted ` +
                                  `immediately, so it will stop appearing in answers.`,
                              )
                            ) {
                              removeDocument.mutate(d.id);
                            }
                          }}
                        >
                          <Trash2 size={14} aria-hidden />
                        </button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </PanelBody>
        </Panel>
      </PageFill>
    </Page>
  );
}

/**
 * Natural Input Library Search, as a rail beside the catalogue.
 *
 * It used to be a card above the table, roughly 380px of it, which meant the
 * table below started below the fold and the results pushed it further down
 * with every search. Here it keeps its own height, and its results scroll
 * within the rail rather than moving the table at all.
 */
function NaturalSearch() {
  const search = useLibrarySearchMutation();
  const history = useLibrarySearchHistoryQuery();
  const [query, setQuery] = useState("");
  const recent = history.data?.searches.slice(0, 8) ?? [];
  const runAgain = (q: string) => {
    setQuery(q);
    search.mutate({ query: q });
  };
  // Stacked above the table it is a card that takes only the room it needs;
  // beside it, it is a full-height pane. Which one is a question about
  // behaviour — whether the results list grows or scrolls — so CSS alone
  // cannot answer it.
  const wide = useMediaQuery(XXL);
  const hasResults = search.data !== undefined || search.isError;

  return (
    <Panel className={wide ? "min-h-0" : "flex-none"}>
      <PanelHeader className="flex-col items-start gap-0.5 py-2">
        <PanelTitle>Natural Input Library Search</PanelTitle>
        <span className="text-xs text-muted-foreground">
          Finds documents by meaning, in plain language
        </span>
      </PanelHeader>

      <div className="shrink-0 border-b border-border p-3">
        <Textarea
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Describe what you'd like me to find"
          rows={3}
          onKeyDown={(e) => {
            // The textarea is for a sentence, not a paragraph, and the thing
            // people do after typing a sentence is press Enter.
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && query.trim()) {
              e.preventDefault();
              search.mutate({ query: query.trim() });
            }
          }}
        />
        <div className="mt-2 flex items-center justify-end gap-3">
          {search.data && (
            <button
              type="button"
              className="text-xs text-muted-foreground hover:text-foreground"
              onClick={() => search.reset()}
            >
              Clear results
            </button>
          )}
          <Button
            variant="primary"
            size="sm"
            className="text-xs"
            disabled={!query.trim() || search.isPending}
            onClick={() => search.mutate({ query: query.trim() })}
          >
            {search.isPending ? "Searching…" : "Search"}
          </Button>
        </div>
      </div>

      {/* Nothing to show and nowhere to show it: stacked, an empty results
          pane is a strip of white between the search box and the table. */}
      {(wide || hasResults) && (
      <PanelBody className={wide ? "p-3" : "max-h-64 p-3"}>
        {search.isError && (
          <p className="text-sm text-danger-text">
            The search could not be run. Try again in a moment.
          </p>
        )}

        {!hasResults && recent.length === 0 && (
          <div className="flex h-full min-h-[8rem] flex-col items-center justify-center gap-2 text-center text-xs text-muted-foreground">
            <Search size={18} aria-hidden className="opacity-40" />
            <p className="max-w-[24ch]">
              Results appear here, ranked by how close they are to what you described.
            </p>
          </div>
        )}

        {/* Only the person's own searches; the server never returns anyone
            else's. Clicking one runs it again rather than replaying the old
            results, because the library may have changed since. */}
        {!hasResults && recent.length > 0 && (
          <div>
            <h3 className="mb-1 px-2 text-xs font-medium text-muted-foreground">Your recent searches</h3>
            <ul className="space-y-0.5">
              {recent.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="flex w-full items-baseline justify-between gap-3 rounded-md px-2 py-1.5 text-left transition-colors duration-100 ease-snap hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                    disabled={search.isPending}
                    onClick={() => runAgain(s.query)}
                  >
                    <span className="line-clamp-1 text-sm">{s.query}</span>
                    <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
                      {s.resultCount} result{s.resultCount === 1 ? "" : "s"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {search.data && search.data.results.length === 0 && (
          <p className="text-sm text-muted-foreground">
            Nothing in the library is close enough to that description.
          </p>
        )}

        {search.data && search.data.results.length > 0 && (
          <ul className="space-y-1">
            {search.data.results.map((r) => (
              <li key={r.id}>
                {/* A new tab, so the results stay put while someone reads down
                    them. rel="opener" is not an oversight: target="_blank"
                    implies noopener, and a noopener tab starts with empty
                    sessionStorage — no sign-in and no chosen org, so it would
                    redirect through Cognito and could land in another org. A
                    tab with an opener gets a copy. It is our own origin, so
                    there is nothing to protect the opener from. */}
                <Link
                  to="/library/$documentId"
                  params={{ documentId: r.id }}
                  search={r.bestPage !== null ? { page: r.bestPage } : {}}
                  target="_blank"
                  rel="opener"
                  className="block w-full rounded-md p-2 text-left transition-colors duration-100 ease-snap hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="text-sm font-medium text-primary">{r.title}</span>
                    <span className="shrink-0 font-mono text-[10px] tabular-nums text-muted-foreground">
                      {r.score.toFixed(3)}
                      {r.bestPage !== null && ` · p. ${r.bestPage}`}
                    </span>
                  </div>
                  <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{r.snippet}</p>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </PanelBody>
      )}
    </Panel>
  );
}

/**
 * A cell of extracted entities.
 *
 * Three of these columns on a table with ten of them. The cap used to be two
 * names regardless of the space available, which on a wide screen left most of
 * the column empty and the count still reading "+7"; it now fills the width it
 * has and clamps at two lines, so the density follows the window. The title
 * attribute carries the rest for a reader who hovers.
 *
 * Undefined rather than empty is the case where the server is older than this
 * build and does not send the field at all; both render as a dash, because the
 * distinction is not one a librarian can act on.
 */
function EntityList({ items }: { items?: string[] }) {
  if (!items || items.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <span
      className="line-clamp-2 text-xs leading-snug text-muted-foreground"
      title={items.join(", ")}
    >
      {items.join(", ")}
    </span>
  );
}
